import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import BenchlingConfig from 'BenchlingConfig';
import * as WarehouseConstants from 'constants/WarehouseConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import {readableTime} from 'util/DateUtil';
import sortedBy from 'util/sortedBy';

import CreateWarehouseLoginModal from './CreateWarehouseLoginModal';
import {CreateLoginModalState} from './reduceWarehouseUI';
import * as WarehouseActions from './WarehouseActions';

/**
 * Sort logins for rendering:
 * - permanent, non-revoked (by createdAt desc)
 * - temporary, non-revoked (by expiresAt desc, createdAt desc)
 * - revoked (by createdAt desc)
 *
 * Exported for tests
 *
 * @param logins
 */
export function sortLogins(
  logins: Array<WarehouseConstants.WarehouseLogin>
): Array<WarehouseConstants.WarehouseLogin> {
  const nonRevokedPermanent = sortedBy(
    logins.filter((login) => !login.revoked && !login.expiresAt),
    [{accessor: 'createdAt', reversed: true}]
  );
  const nonRevokedTemp = sortedBy(
    logins.filter((login) => !login.revoked && login.expiresAt),
    [
      {accessor: 'expiresAt', reversed: true},
      {accessor: 'createdAt', reversed: true},
    ]
  );
  const revoked = sortedBy(
    logins.filter((login) => login.revoked),
    [{accessor: 'createdAt', reversed: true}]
  );
  return [...nonRevokedPermanent, ...nonRevokedTemp, ...revoked];
}

interface ConnectedProps {
  logins: Array<WarehouseConstants.WarehouseLogin>;
  createLoginModal: CreateLoginModalState;
}

function mapStateToProps(state: BenchlingState): ConnectedProps {
  return {
    // eslint-disable-next-line no-restricted-syntax
    logins: sortLogins(_.values(state.models.warehouse.logins)),
    createLoginModal: state.ui.warehouse.createLoginModal,
  };
}

interface DispatchProps {
  onFetchLogins: () => void;
  onCreateLogin: (label: string) => void;
  onRevokeLogin: (login: WarehouseConstants.WarehouseLogin) => void;
  onOpenCreateLoginModal: () => void;
  onCloseCreateLoginModal: () => void;
  onUpdateCreateLoginLabel: (label: string) => void;
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onFetchLogins(): void {
      dispatch(WarehouseActions.fetchLogins());
    },
    onCreateLogin(label: string): void {
      dispatch(WarehouseActions.createLogin(label));
    },
    onRevokeLogin(login: WarehouseConstants.WarehouseLogin): void {
      dispatch(WarehouseActions.revokeLogin(login));
    },
    onOpenCreateLoginModal(): void {
      dispatch(WarehouseActions.openCreateLoginModal());
    },
    onCloseCreateLoginModal(): void {
      dispatch(WarehouseActions.closeCreateLoginModal());
    },
    onUpdateCreateLoginLabel(label: string): void {
      dispatch(WarehouseActions.updateCreateLoginLabel(label));
    },
  };
}

const WAREHOUSE_ROTATION_CUTOFF_DATE: string = '2024-03-06';
const WAREHOUSE_ROTATION_DOCUMENTATION_LINK: string =
  'https://help.benchling.com/hc/en-us/articles/24833307834893-Warehouse-CA-and-credentials-upgrade-early-2024';
interface WarehouseLoginProps {
  dataTestAttrs?: DataTestAttrs;
  login: WarehouseConstants.WarehouseLogin;
  handleRevoke: () => void;
}

export function WarehouseLogin({dataTestAttrs, login, handleRevoke}: WarehouseLoginProps): JSX.Element {
  const hasLabel = login.label.length > 0;
  return (
    <div
      className="u-small-spacing-bottom"
      {...getTestProps(dataTestAttrs ?? {component: 'WarehouseLogin', element: 'root'})}
    >
      {!login.revoked && login.createdAt < WAREHOUSE_ROTATION_CUTOFF_DATE && (
        <div className="pull-left">
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={
              <span>
                This login needs to be recreated. See more in our{' '}
                <a href={WAREHOUSE_ROTATION_DOCUMENTATION_LINK}>documentation</a>.
              </span>
            }
          />
        </div>
      )}
      {!login.revoked && (
        <div className="pull-right">
          <Tooltip container="body" title="Revoke Credentials">
            <BenchlingIcon className="u-danger-pointer u-faded" icon="trash-can" onClick={handleRevoke} />
          </Tooltip>
        </div>
      )}
      <div className={classNames({'u-faded': login.revoked})}>
        <span className="font-proxima-semibold">{login.username}</span>
        <span>{hasLabel && ` ${login.label}`}</span>
      </div>
      <div className="u-faded">{login.revoked ? 'Revoked' : `Created ${readableTime(login.createdAt)}`}</div>
      {!login.revoked && login.expiresAt && (
        <div className="u-faded">Expires {readableTime(login.expiresAt)}</div>
      )}
    </div>
  );
}

export class WarehouseLogins extends React.Component<ConnectedProps & DispatchProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  override componentDidMount(): void {
    this.props.onFetchLogins();
  }

  _handleOpenCreateModal = (): void => {
    this.props.onOpenCreateLoginModal();
  };

  _handleCloseCreateModal = (): void => {
    this.props.onCloseCreateLoginModal();
  };

  override render(): JSX.Element {
    assert(
      BenchlingConfig.ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS,
      'Should not render warehouse credentials create without ' +
        'ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS flag set to true'
    );
    return (
      <div>
        <ModalAnchor open={this.props.createLoginModal.open}>
          <CreateWarehouseLoginModal
            createLoginModal={this.props.createLoginModal}
            onLabelChange={this.props.onUpdateCreateLoginLabel}
            onCreate={this.props.onCreateLogin}
            onRequestClose={this._handleCloseCreateModal}
          />
        </ModalAnchor>
        <h2>Warehouse Credentials</h2>
        {this.props.logins ? (
          // Hide expired logins that haven't been revoked explicitly
          this.props.logins
            .filter((login) => login.revoked || !login.expired)
            .map((login) => (
              <WarehouseLogin
                dataTestAttrs={{component: 'WarehouseLogin', element: 'root', key: login.id}}
                key={login.id}
                login={login}
                handleRevoke={() => {
                  this.props.onRevokeLogin(login);
                }}
              />
            ))
        ) : (
          <div className="u-faded u-small-spacing-bottom">Loading...</div>
        )}
        {this.props.logins && this.props.logins.length === 0 && (
          <div className="u-faded u-small-spacing-bottom">Create a set of credentials to get started.</div>
        )}
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.SMALL}
          text="Create Credentials"
          onClick={this._handleOpenCreateModal}
        />
        <ExportAuditLogButton
          className="u-spacing-left"
          buttonSize={ButtonSize.SMALL}
          ids={this.props.logins.map((login) => login.id)}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseLogins);
